import React, { useState } from 'react';
import { fetchDevelopers, addDeveloper, removeDeveloper } from '@/data/api/cas';
import { TDeveloperUser } from '@/data/types/cas';
import { Button } from '@/lib/components/ui/button';
import { Input } from '@/lib/components/ui/input';
import useNotify from '@/hooks/useNotify';
import { Trash2, UserPlus, CheckCircle, ShieldAlert, Code2 } from 'lucide-react';
import { motion } from 'motion/react';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { InfiniteScroll } from '@/components/common/InfiniteScroll/InfiniteScroll';

export const Developers: React.FC = () => {
  const notify = useNotify();
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleFetchDevelopers = async (params: any) => {
    try {
      const res = await fetchDevelopers(params);
      if (!res.items) return { payload: [], type: 'api/direct' };
      return { payload: res.items, type: 'api/direct' };
    } catch (err) {
      console.error('Failed to fetch developers:', err);
      throw err;
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) return;

    try {
      setAdding(true);
      await addDeveloper(newEmail);
      notify.success('Developer added successfully');
      setNewEmail('');
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      let code = '';
      try {
        const parsed = JSON.parse(err);
        code = parsed.code;
      } catch (e) {}
      
      if (code === 'CAS_USER_NOT_FOUND') {
        notify.error('User not found. They must sign up first.');
      } else if (code === 'CAS_ALREADY_A_DEVELOPER') {
        notify.success('User is already a developer');
        setNewEmail('');
        setRefreshKey(k => k + 1);
      } else {
        notify.error('Failed to add developer');
      }
    } finally {
      setAdding(false);
    }
  };

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [developerToRemove, setDeveloperToRemove] = useState<string | null>(null);

  const handleRemove = (id: string) => {
    setDeveloperToRemove(id);
    setIsConfirmOpen(true);
  };

  const executeRemove = async () => {
    if (!developerToRemove) return;
    try {
      await removeDeveloper(developerToRemove);
      notify.success('Developer removed successfully');
      setRefreshKey(k => k + 1);
    } catch (err: any) {
      let code = '';
      try {
        const parsed = JSON.parse(err);
        code = parsed.code;
      } catch (e) {}

      if (code === 'CAS_ROOT_DEVELOPER') {
        notify.error('Cannot remove the root developer through the API.');
      } else {
        notify.error('Failed to remove developer');
      }
    } finally {
      setIsConfirmOpen(false);
      setDeveloperToRemove(null);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8 max-w-5xl"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Developers</h1>
          <p className="text-gray-500 mt-1 font-medium">
            Manage system administrators who have access to this dashboard.
          </p>
        </div>
        
        <form onSubmit={handleAdd} className="flex gap-3 w-full md:w-auto relative">
          <Input
            type="email"
            placeholder="Invite developer by email..."
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            className="h-10 w-full md:w-72 bg-white/70 backdrop-blur-sm border-gray-200 shadow-sm rounded-xl focus-visible:ring-blue-500 text-sm"
          />
          <Button type="submit" disabled={adding || !newEmail} className="h-10 rounded-xl px-4 bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-600/20 text-sm">
            {adding ? 'Adding...' : <><UserPlus className="w-4 h-4 mr-2" /> Add</>}
          </Button>
        </form>
      </div>

      <InfiniteScroll<TDeveloperUser>
        key={refreshKey}
        apiAction={handleFetchDevelopers}
        itemsData={[]}
        useDirectResponse={true}
        renderEmpty={() => (
          <div className="py-16 text-center bg-white/50 backdrop-blur-md rounded-3xl border border-gray-200/50 border-dashed">
            <Code2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No developers found</p>
          </div>
        )}
      >
        {(developers) => (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {developers.map((dev, i) => (
              <motion.div 
                key={dev.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                className="relative flex flex-col bg-white/70 backdrop-blur-xl border border-gray-200/60 rounded-3xl p-6 shadow-lg shadow-gray-200/20"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden shadow-inner">
                      {dev.avatar || dev.image_url ? (
                        <img src={dev.avatar || dev.image_url || undefined} alt="Avatar" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold text-gray-500">
                          {dev.first_name?.charAt(0)}{dev.last_name?.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 tracking-tight">{dev.first_name} {dev.last_name}</h3>
                      <p className="text-xs text-gray-500 font-medium">{dev.email}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-2 mb-6">
                  {dev.root ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800 border border-purple-200/50">
                      <ShieldAlert className="w-3.5 h-3.5" /> Root Developer
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200/50">
                      <CheckCircle className="w-3.5 h-3.5" /> Developer
                    </span>
                  )}
                </div>

                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100/80">
                  <div className="text-xs text-gray-400 font-medium">
                    Added {new Date(dev.created_at).toLocaleDateString()}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 text-xs font-semibold px-3 rounded-lg"
                    onClick={() => handleRemove(dev.id)}
                    disabled={dev.root}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Revoke
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </InfiniteScroll>

      <ConfirmationModal
        isOpen={isConfirmOpen}
        setIsOpen={setIsConfirmOpen}
        title="Remove Developer"
        message="Are you sure you want to remove this developer? They will lose access to the admin dashboard immediately."
        confirmText="Remove Developer"
        type="danger"
        onConfirm={executeRemove}
      />
    </motion.div>
  );
};
